package ui.resource

import androidx.compose.runtime.Composable
import androidx.compose.runtime.MutableState
import androidx.compose.runtime.remember
import androidx.compose.ui.graphics.vector.ImageVector
import api.ResourceOrientation
import api.ResourceType
import common.frontend.api.Io
import compose.assets.Svg
import compose.assets.svg.MoveResize
import compose.assets.svg.Multimedia
import compose.assets.svg.ScreenLand
import compose.assets.svg.ScreenPort
import org.jetbrains.compose.resources.StringResource
import provider.CommonDisplayProvider
import provider.LocalAccountProvider
import provider.LocalDisplayProvider
import ssoft.frontend.generated.resources.*
import tk.mallumo.compose.navigation.LocalNavigation
import tk.mallumo.compose.navigation.viewmodel.globalViewModel
import tk.mallumo.compose.navigation.viewmodel.viewModel
import tools.EFieldList
import tools.EFieldTextString
import tools.labelRes
import ui.GlobalVM
import ui.widget.DropDownResItem


interface ResourceListingScope {

    val vmGlobal: GlobalVM
    val vm: ResourceListingVM
    val action: (ResourceListingVM.Action) -> Unit
    val login: Io.User
    val args: ResourceListingArgs
    val resTypes: List<PickerType>
    val resOrientations: List<PickerOrientation>
    val display: CommonDisplayProvider
    val multiselect: Boolean
    val excluded: List<Long>
    val onPick: ((Set<Io.ListingResource.Item>) -> Unit)?

    companion object {

        @Composable
        fun rememberScope(
            args: ResourceListingArgs,
            multiselect: Boolean,
            excluded: List<Long>,
            onPick: ((Set<Io.ListingResource.Item>) -> Unit)? = null
        ): ResourceListingScope {
            val account = LocalAccountProvider.current
            val display = LocalDisplayProvider.current
//            TODO("MAKE IT only as local VM")
            val nav = runCatching {
                LocalNavigation.current
            }.getOrNull()
//            TODO("MAKE IT only as local VM")
            val vm = nav?.viewModel(ResourceListingVM::class, args.companyId.toString())
                ?: globalViewModel(ResourceListingVM::class, args.companyId.toString())

            val vmGlobal = globalViewModel(GlobalVM::class)
            val action = ResourceListingVM.Action.rememberAction(vm)

            val resTypes = remember(args.restrictionType) {
                buildPickerTypes(args)
            }
            val resOrientations = remember(args.restrictionOrientation) {
                buildPickerOrientations(args)
            }
            return remember {
                object : ResourceListingScope {
                    override val vmGlobal: GlobalVM
                        get() = vmGlobal
                    override val vm: ResourceListingVM
                        get() = vm
                    override val action: (ResourceListingVM.Action) -> Unit
                        get() = action
                    override val login: Io.User
                        get() = account.current!!
                    override val args: ResourceListingArgs
                        get() = args
                    override val display: CommonDisplayProvider
                        get() = display
                    override val multiselect: Boolean
                        get() = multiselect
                    override val excluded: List<Long>
                        get() = excluded
                    override val onPick: ((Set<Io.ListingResource.Item>) -> Unit)?
                        get() = onPick
                    override val resTypes: List<PickerType>
                        get() = resTypes
                    override val resOrientations: List<PickerOrientation>
                        get() = resOrientations
                }.also {
                    vm.init(it)
                }
            }
        }

        private fun buildPickerTypes(args: ResourceListingArgs) = ResourceType.all
            .filterNot { it == ResourceType.TEXT }
            .filterNot { it == ResourceType.WWW_APP }
            .filterNot { it == ResourceType.ICON }
            .filterNot { it == ResourceType.RAW }
            .filter { args.restrictionType == ResourceType.UNDEFINED.mask || (it match args.restrictionType) }
            .map {
                PickerType(
                    type = it,
                    image = it.imageVector()
                )
            }.let {
                if (it.size > 1 && it.none { it.type == ResourceType.UNDEFINED }) {
                    listOf(
                        PickerType(
                            type = ResourceType.UNDEFINED,
                            image = Svg.Multimedia
                        )
                    ) + it
                } else it
            }

        private fun buildPickerOrientations(args: ResourceListingArgs): List<PickerOrientation> {
            return ResourceOrientation.all
                .filter { args.restrictionOrientation == ResourceOrientation.UNDEFINED.mask || (it match args.restrictionOrientation) }
                .map {
                    PickerOrientation(
                        type = it,
                        image = when (it) {
                            ResourceOrientation.UNDEFINED -> Svg.MoveResize
                            ResourceOrientation.HORIZONTAL -> Svg.ScreenLand
                            ResourceOrientation.VERTICAL -> Svg.ScreenPort
                        }
                    )
                }.let {
                    if (it.size > 1 && it.none { it.type == ResourceOrientation.UNDEFINED }) {
                        listOf(
                            PickerOrientation(
                                type = ResourceOrientation.UNDEFINED,
                                image = Svg.MoveResize
                            )
                        ) + it
                    } else it
                }
        }
    }


    class Wrapper(
        val canAdd: Boolean,
        val canEdit: Boolean,
        val canCustomVideoType: Boolean,
        val selectedType: MutableState<PickerType>,
        listing: Io.ListingResource,
        val space: Io.ListingResource.Space = listing.space
    ) : EFieldList<Io.ListingResource.Item, Wrapper.Item>(
        items = listing.items,
        onBindEntry = { Item(it) }
    ) {


        fun canAddNewResource() = Io.ListingResource.Space.LIMIT_SPACE > space.total

        class Item(
            override val data: Io.ListingResource.Item,
            override val key: String = data.id.toString(),
        ) : Entry<Io.ListingResource.Item> {

            val name: EFieldTextString = EFieldTextString(
                property = data::name,
                condition = { it.trim().takeIf { it.length > 4 } })

        }
    }


    class PickerType(
        val type: ResourceType,
        override val label: StringResource = type.labelRes,
        override val image: ImageVector? = null,
        var customVideoFormat: Boolean = false
    ) : DropDownResItem {
        fun match(it: Wrapper.Item): Boolean = when (type) {
            ResourceType.RAW,
            ResourceType.UNDEFINED -> true

            ResourceType.VIDEO,
            ResourceType.VIDEO_ALPHA,
            ResourceType.IMAGE,
            ResourceType.WWW_APP,
            ResourceType.TEXT,
            ResourceType.ICON,
            ResourceType.AUDIO,
            ResourceType.DOCUMENT -> type match it.data.type

        }

        companion object {
            val customVideoType by lazy {
                PickerType(
                    type = ResourceType.VIDEO,
                    label = Res.string.global__resource_type__video_not_converted,
                    image = ResourceType.VIDEO.imageVector(),
                    customVideoFormat = true
                )
            }
        }
    }

    class PickerOrientation(
        val type: ResourceOrientation,
        override val label: StringResource = when (type) {
            ResourceOrientation.HORIZONTAL -> Res.string.global__orientation__horizontal
            ResourceOrientation.UNDEFINED -> Res.string.global__orientation__all
            ResourceOrientation.VERTICAL -> Res.string.global__orientation__vertical
        },
        override val image: ImageVector? = null,
    ) : DropDownResItem {
        fun match(it: Wrapper.Item): Boolean = when (type) {
            ResourceOrientation.UNDEFINED -> true
            ResourceOrientation.HORIZONTAL -> it.data.width >= it.data.height
            ResourceOrientation.VERTICAL -> it.data.width < it.data.height
        }
    }
}
